import { useEffect, useRef, useState, type ComponentType } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Checkbox,
  Label,
  PageHeaderActionPrimary,
  PageHeaderActionSecondary,
  formatRelativeTimeIso,
} from '@yeshuman/sdk'
import { useQueryClient } from '@tanstack/react-query'
import { Gauge, Tags } from 'lucide-react'
import { toast } from 'sonner'

import {
  invalidatePlatformArea,
  useBackfill,
  type BackfillKind,
  type BackfillRun,
} from '../../hooks/use-platform-area'
import { ActionStatusBadge } from './platform-area-parts'

const COPY: Record<
  BackfillKind,
  {
    name: string
    icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>
    run: string
    resume: string
    running: string
    dialogTitle: string
    dialogBody: string
    redoAll: string
    started: string
    progress: (processed: number, total: number) => string
  }
> = {
  completeness: {
    name: 'Completeness backfill',
    icon: Gauge,
    run: 'Run completeness backfill',
    resume: 'Resume completeness backfill',
    running: 'Backfill running…',
    dialogTitle: 'Run completeness backfill?',
    dialogBody:
      'Scores every real job that has no completeness score yet, a few at a time. Simulated jobs are left out. If the server restarts mid-run, run it again to carry on where it stopped.',
    redoAll: 'Rescore all jobs, including ones already scored',
    started: 'Completeness backfill started. Progress shows above the list.',
    progress: (processed, total) => `Scored ${processed} of ${total} jobs`,
  },
  'marketplace-skills': {
    name: 'Marketplace skills backfill',
    icon: Tags,
    run: 'Run skills backfill',
    resume: 'Resume skills backfill',
    running: 'Skills backfill running…',
    dialogTitle: 'Run marketplace skills backfill?',
    dialogBody:
      'Reads the skills in every active Adzuna, Jooble and CareerJet listing that has not been read yet, a few at a time, and links them to the skill lexicon. Terms the lexicon does not know become proposals for review. Each listing is one language-model call, so a full run takes a while. If the server restarts mid-run, run it again to carry on where it stopped.',
    redoAll: 'Re-read all listings, including ones already read',
    started: 'Marketplace skills backfill started. Progress shows above the list.',
    progress: (processed, total) => `Read ${processed} of ${total} listings`,
  },
}

export function BackfillButton({ kind, primary = false }: { kind: BackfillKind; primary?: boolean }) {
  const copy = COPY[kind]
  const { status, start } = useBackfill(kind)
  const [open, setOpen] = useState(false)
  const [redoAll, setRedoAll] = useState(false)
  const running = status.data?.status === 'running'
  const resumable = status.data?.status === 'interrupted'
  const Action = primary ? PageHeaderActionPrimary : PageHeaderActionSecondary
  const Icon = copy.icon

  const confirm = () => {
    start.mutate(redoAll, {
      onSuccess: () => toast(copy.started),
      onError: (err) => toast.error(err.message),
    })
    setOpen(false)
    setRedoAll(false)
  }

  return (
    <>
      <Action type="button" disabled={running || start.isPending} onClick={() => setOpen(true)}>
        <Icon className="h-4 w-4" aria-hidden />
        {running ? copy.running : resumable ? copy.resume : copy.run}
      </Action>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{copy.dialogTitle}</AlertDialogTitle>
            <AlertDialogDescription>{copy.dialogBody}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-start gap-2">
            <Checkbox
              id={`platform-backfill-${kind}-redo-all`}
              checked={redoAll}
              onCheckedChange={(value) => setRedoAll(value === true)}
            />
            <Label htmlFor={`platform-backfill-${kind}-redo-all`} className="text-sm font-normal leading-snug">
              {copy.redoAll}
            </Label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirm}>Run backfill</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function percent(run: BackfillRun): number {
  const total = run.result.total ?? 0
  if (!total) return run.status === 'running' ? 0 : 100
  return Math.min(100, Math.round(((run.result.processed ?? 0) / total) * 100))
}

/** Progress for the latest run of a backfill; refreshes the jobs list when a run finishes. */
export function BackfillProgress({ kind }: { kind: BackfillKind }) {
  const copy = COPY[kind]
  const queryClient = useQueryClient()
  const { status } = useBackfill(kind)
  const run = status.data
  const wasRunning = useRef(false)

  useEffect(() => {
    if (!run) return
    if (wasRunning.current && run.status !== 'running') {
      invalidatePlatformArea(queryClient)
      toast(run.summary || `${copy.name} finished.`)
    }
    wasRunning.current = run.status === 'running'
  }, [run, queryClient, copy.name])

  if (!run) return null

  return (
    <div className="space-y-2 rounded-lg border bg-muted/30 p-3" role="status" aria-live="polite">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium">{copy.name}</span>
        <ActionStatusBadge status={run.status} />
        <span className="text-muted-foreground">
          {run.status === 'running'
            ? copy.progress(run.result.processed ?? 0, run.result.total ?? 0)
            : `${run.summary} ${formatRelativeTimeIso(run.finished_at ?? run.started_at)}`}
        </span>
      </div>
      {run.status === 'running' ? (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
          <div className="h-full rounded-full bg-primary transition-[width]" style={{ width: `${percent(run)}%` }} />
        </div>
      ) : null}
    </div>
  )
}
